import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useClub } from '@/contexts/ClubContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import PageSpinner from '@/components/PageSpinner';
import { PageHeaderSkeleton, TableSkeleton } from '@/components/PageSkeletons';
import { Calendar, Grid2X2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PackageFeatureGate from '@/components/PackageFeatureGate';
import StarRating from '@/components/StarRating';
import { billingService, feedbackService, matchService, memberService } from '@/services/storageService';
import { getLatestEvaluation, mapEvaluationsFromApi, mapMembersFromApi } from '@/lib/clubApiMappers';

const pad = (value: number) => (value < 10 ? `0${value}` : `${value}`);
const toDateParam = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

type DailyReportResponse = {
  date: string;
  total_matches: number;
  total_players: number;
  avg_matches_per_player: number;
  matches_by_mode: Record<string, number>;
  players: Array<{
    member_id: number;
    first_name: string;
    last_name: string;
    total_matches: number;
    matches_by_mode: Record<string, number>;
    opponents: Record<string, number>;
    unique_opponents: number;
    unique_partners: number;
  }>;
  opponent_matrix: Record<string, Record<string, Record<string, number>>>;
  player_names: Record<string, { first_name: string; last_name: string }>;
};

const HUMAN_MODE_LABELS: Record<string, string> = {
  ROUND_ROBIN: 'Round Robin',
  SKILL_BASED: 'Skill Based',
  BALANCED_TEAM: 'Balanced',
  PLAYERS_PICK: 'Players Pick',
  UNKNOWN: 'Unknown',
};

const normalizeModeLabel = (mode: string) => HUMAN_MODE_LABELS[mode] || mode.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

export default function DailyMatchReport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeClubId, currentClubRole } = useClub();
  const clubId = activeClubId ? Number(activeClubId) : null;
  const [selectedDate, setSelectedDate] = React.useState(() => new Date());
  const [showMatrix, setShowMatrix] = React.useState(true);
  const dateParam = toDateParam(selectedDate);
  const tzOffset = React.useMemo(() => new Date().getTimezoneOffset(), []);

  const membersQuery = useQuery({
    queryKey: ['club-members', clubId],
    queryFn: () => memberService.list(clubId as number) as Promise<any[]>,
    enabled: Boolean(clubId && user),
  });

  const evalsQuery = useQuery({
    queryKey: ['club-evals', clubId],
    queryFn: () => feedbackService.listForClub(clubId as number),
    enabled: Boolean(clubId && user),
  });

  const reportQuery = useQuery({
    queryKey: ['daily-report', clubId, dateParam, tzOffset],
    queryFn: () => matchService.getDailyReport(clubId as number, dateParam, tzOffset) as Promise<DailyReportResponse>,
    enabled: Boolean(clubId && user),
  });
  const billingQuery = useQuery({
    queryKey: ['billing-status', clubId],
    queryFn: () => billingService.getStatus(clubId),
    enabled: Boolean(user && clubId),
  });

  const resolvedPackageCode = String(
    billingQuery.data?.package || billingQuery.data?.recommended_package || 'package1',
  )
    .trim()
    .toLowerCase();
  const normalizedRole = String(currentClubRole || user?.role || '').trim().toLowerCase();
  const canUpgradePackage = normalizedRole === 'admin';
  const isDailyReportLockedByPackage = resolvedPackageCode === 'package1';
  const report = isDailyReportLockedByPackage ? undefined : reportQuery.data;
  const members = React.useMemo(
    () => (isDailyReportLockedByPackage ? [] : mapMembersFromApi((membersQuery.data || []) as any[])),
    [isDailyReportLockedByPackage, membersQuery.data],
  );
  const evalsByMember = React.useMemo(() => {
    if (isDailyReportLockedByPackage) return {};
    const grouped: Record<string, any[]> = {};
    (evalsQuery.data || []).forEach((ev: any) => {
      const id = String(ev.player_id ?? ev.playerId ?? '');
      if (!id) return;
      if (!grouped[id]) grouped[id] = [];
      grouped[id].push(ev);
    });
    return grouped;
  }, [evalsQuery.data, isDailyReportLockedByPackage]);
  const ratingsByMember = React.useMemo(() => {
    const map: Record<string, number | null> = {};
    members.forEach((member) => {
      const evals = mapEvaluationsFromApi(evalsByMember[member.id] || []);
      const latest = getLatestEvaluation(evals);
      map[member.id] = latest?.rating ?? null;
    });
    return map;
  }, [members, evalsByMember]);
  const players = report?.players || [];
  const modes = React.useMemo(() => {
    const set = new Set<string>();
    Object.keys(report?.matches_by_mode || {}).forEach((mode) => set.add(mode));
    players.forEach((player) => Object.keys(player.matches_by_mode || {}).forEach((mode) => set.add(mode)));
    return Array.from(set.values());
  }, [report, players]);
  const sortedModes = React.useMemo(() => {
    const priority = ['ROUND_ROBIN', 'SKILL_BASED', 'BALANCED_TEAM', 'PLAYERS_PICK'];
    return [...modes].sort((a, b) => {
      const aIndex = priority.indexOf(a);
      const bIndex = priority.indexOf(b);
      if (aIndex !== -1 || bIndex !== -1) {
        return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
      }
      return a.localeCompare(b);
    });
  }, [modes]);

  const [matrixMode, setMatrixMode] = React.useState('ALL');
  React.useEffect(() => {
    if (!report) return;
    if (matrixMode === 'ALL') return;
    if (!report.opponent_matrix?.[matrixMode]) {
      setMatrixMode('ALL');
    }
  }, [report, matrixMode]);

  const nameForId = React.useCallback(
    (id: string) => {
      const info = report?.player_names?.[id];
      if (!info) return id;
      return `${info.first_name} ${info.last_name}`.trim() || id;
    },
    [report],
  );
  const renderStars = (memberId: string, size = 10) => {
    const rating = ratingsByMember[memberId];
    const roleRaw = currentClubRole || user?.role || '';
    const normalizedRole = String(roleRaw).trim().toLowerCase();
    const isCoach = normalizedRole === 'coach';
    if (rating === null || rating === undefined) {
      if (isCoach) {
        return <StarRating value={5} size={size} />;
      }
      return (
        <span className="opacity-40">
          <StarRating value={0} size={size} />
        </span>
      );
    }
    return <StarRating value={rating} size={size} />;
  };

  const sortedPlayerIds = React.useMemo(() => {
    const list = [...players];
    list.sort((a, b) => {
      const nameA = `${a.last_name || ''} ${a.first_name || ''}`.trim().toLowerCase();
      const nameB = `${b.last_name || ''} ${b.first_name || ''}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
    return list.map((p) => String(p.member_id));
  }, [players]);

  if (reportQuery.isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHeaderSkeleton />
        <TableSkeleton rows={6} />
      </div>
    );
  }

  if (reportQuery.isError) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Failed to load the daily report.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="relative min-h-[calc(100vh-8rem)]">
      <div
        className={`space-y-6 animate-fade-in transition-all ${
          isDailyReportLockedByPackage ? 'blur-[6px] pointer-events-none select-none' : ''
        }`}
      >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-foreground flex items-center gap-3">
            <Grid2X2 className="text-primary shrink-0" />
            Daily Match Report
          </h1>
          <p className="text-sm text-muted-foreground">Only matches that started and finished on the selected date.</p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="text-muted-foreground" size={18} />
          <Input
            type="date"
            value={dateParam}
            onChange={(event) => {
              const value = event.target.value;
              if (!value) return;
              const [y, m, d] = value.split('-').map((v) => Number(v));
              const next = new Date();
              next.setFullYear(y, m - 1, d);
              next.setHours(12, 0, 0, 0);
              setSelectedDate(next);
            }}
            className="w-[180px]"
          />
        </div>
      </div>

        {!report ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">No report data.</CardContent>
        </Card>
        ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground">Total Matches</p>
                <p className="text-2xl font-semibold">{report.total_matches}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground">Players Played</p>
                <p className="text-2xl font-semibold">{report.total_players}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground">Avg Matches / Player</p>
                <p className="text-2xl font-semibold">{report.avg_matches_per_player}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 space-y-1">
                <p className="text-xs text-muted-foreground">Matches by Selection</p>
                {Object.keys(report.matches_by_mode || {}).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No matches</p>
                ) : (
                  Object.entries(report.matches_by_mode).map(([mode, count]) => (
                    <p key={mode} className="text-sm">
                      {normalizeModeLabel(mode)}: <span className="font-semibold">{count}</span>
                    </p>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Player Summary</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <p className="text-xs text-muted-foreground mb-3">
                Unique opponents can be higher than total matches (each match has two opponents).
              </p>
              {players.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No matches played on this date.</p>
              ) : (
                <table className="w-full text-sm border-separate border-spacing-y-2">
                  <thead className="text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="text-left px-3">Player</th>
                      <th className="text-left px-3">Total</th>
                      {sortedModes.map((mode) => (
                        <th key={mode} className="text-left px-3">
                          {normalizeModeLabel(mode)}
                        </th>
                      ))}
                      <th className="text-left px-3">Unique Opponents</th>
                      <th className="text-left px-3">Unique Partners</th>
                    </tr>
                  </thead>
                  <tbody>
                    {players.map((player) => {
                      return (
                        <tr key={player.member_id} className="bg-muted/40">
                          <td className="px-3 py-2 font-medium">
                            <div className="flex flex-col">
                              <span>
                                {player.first_name} {player.last_name}
                              </span>
                              <span className="mt-1">{renderStars(String(player.member_id))}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2">{player.total_matches}</td>
                          {sortedModes.map((mode) => (
                            <td key={mode} className="px-3 py-2">
                              {player.matches_by_mode?.[mode] || 0}
                            </td>
                          ))}
                          <td className="px-3 py-2">{player.unique_opponents ?? 0}</td>
                          <td className="px-3 py-2">{player.unique_partners ?? 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          <Card className="w-full">
            <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle>Opponents Matrix</CardTitle>
              <div className="flex items-center gap-2">
                <select
                  className="border rounded-md px-2 py-1 text-sm bg-background"
                  value={matrixMode}
                  onChange={(event) => setMatrixMode(event.target.value)}
                >
                  <option value="ALL">ALL</option>
                  {sortedModes.map((mode) => (
                    <option key={mode} value={mode}>
                      {normalizeModeLabel(mode)}
                    </option>
                  ))}
                </select>
                <Button variant="outline" size="sm" onClick={() => setShowMatrix((prev) => !prev)}>
                  {showMatrix ? 'Hide' : 'Show'}
                </Button>
              </div>
            </CardHeader>
            {showMatrix && (
              <CardContent className="overflow-x-auto w-full">
                {sortedPlayerIds.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No matches to show.</p>
                ) : (
                  <table className="min-w-[600px] text-xs border">
                  <thead>
                    <tr>
                      <th className="px-2 py-2 border text-left">Player</th>
                      {sortedPlayerIds.map((pid) => (
                        <th key={`head-${pid}`} className="px-2 py-2 border text-left">
                          <div className="flex flex-col gap-1">
                            <span>{nameForId(pid)}</span>
                            <span>{renderStars(pid, 9)}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPlayerIds.map((rowId) => (
                      <tr key={`row-${rowId}`}>
                          <td className="px-2 py-2 border font-medium">
                            <div className="flex flex-col gap-1">
                              <span>{nameForId(rowId)}</span>
                              <span>{renderStars(rowId, 9)}</span>
                            </div>
                          </td>
                        {sortedPlayerIds.map((colId) => {
                          if (rowId === colId) {
                            return (
                              <td key={`cell-${rowId}-${colId}`} className="px-2 py-2 border text-center text-muted-foreground">
                                —
                              </td>
                            );
                          }
                          const count =
                            report.opponent_matrix?.[matrixMode]?.[rowId]?.[colId] ||
                            0;
                          return (
                            <td key={`cell-${rowId}-${colId}`} className="px-2 py-2 border text-center">
                              {count || ''}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            )}
          </Card>
        </>
        )}
      </div>
      {isDailyReportLockedByPackage && (
        <div className="absolute inset-0 z-20 bg-background/30 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6">
          <PackageFeatureGate
            featureName="Daily Match Report"
            currentPackageCode={resolvedPackageCode}
            canUpgrade={canUpgradePackage}
            onUpgrade={() => navigate('/dashboard/club')}
          />
        </div>
      )}
    </div>
  );
}
